import { addYears } from "date-fns";
import { useRef, type ReactNode } from "react";
import type { PropsBase } from "react-day-picker";
import {
    DayPicker,
    getDefaultClassNames,
    type DayPickerProps,
} from "react-day-picker";
import "react-day-picker/dist/style.css";
import { cn } from "../utils/cn";
import { Button } from "./button";

// Custom props extending DayPickerProps, allowing numberOfMonths and className
type CalendarProps = DayPickerProps & {
    readonly mode: PropsBase["mode"];
    readonly containerClassName?: string;
    readonly header?: ReactNode;
};

function Calendar({
    classNames = {},
    containerClassName,
    header,
    captionLayout = "dropdown",
    ...props
}: CalendarProps) {
    const nowRef = useRef(new Date());
    const startMonth = useRef(
        new Date(addYears(nowRef.current, -5).getFullYear(), 1),
    );
    const endMonth = useRef(
        new Date(addYears(nowRef.current, 5).getFullYear(), 1),
    );
    const defaultClassNames = getDefaultClassNames();

    return (
        <div
            className={cn(
                `rounded-lg border border-gray-200 bg-white`,
                containerClassName,
            )}>
            {header}
            <DayPicker
                {...props}
                captionLayout={captionLayout}
                startMonth={startMonth.current}
                endMonth={endMonth.current}
                classNames={{
                    root: cn(defaultClassNames.root, "text-sm line-height-1"),
                    month_grid: "border-collapse space-y-1",
                    day: "size-10",
                    disabled: "opacity-40 cursor-not-allowed",
                    // outside: "border border-amber-500", // Dagar som inte är med i månaden
                    week: "border-t border-b border-neutral",
                    today: "text-blue bg-blue-surface-10 rounded-full underline",
                    ...classNames,
                }}
                modifiersClassNames={{
                    disabled: "opacity-40 cursor-not-allowed",
                    selectedAndToday:
                        "bg-transparent text-primary-copy/70 underline",
                    range_start: "bg-transparent text-primary-copy",
                    range_end: "bg-transparent text-primary-copy",
                    range_middle: "bg-primary-surface-10 text-primary",
                    ...props.modifiersClassNames,
                }}
                components={{
                    DayButton: dayButtonProps => {
                        return (
                            <div className="relative flex flex-row items-center justify-center p-0">
                                <Button
                                    className={cn("size-10 p-5 text-base", {
                                        "rounded-full":
                                            !dayButtonProps.modifiers
                                                .range_middle,
                                    })}
                                    onClick={dayButtonProps.onClick}
                                    disabled={dayButtonProps.disabled}
                                    variant={
                                        dayButtonProps.modifiers.selected ||
                                        dayButtonProps.modifiers
                                            .selectedAndToday ||
                                        dayButtonProps.modifiers.range_start ||
                                        dayButtonProps.modifiers.range_end
                                            ? "primary"
                                            : dayButtonProps.modifiers
                                                    .range_middle
                                              ? "primary-light"
                                              : "ghost"
                                    }>
                                    {dayButtonProps.children}
                                </Button>
                            </div>
                        );
                    },
                }}
            />
        </div>
    );
}

export { Calendar };
export type { CalendarProps };
